import React, { useState, useEffect, useCallback, useMemo } from 'react';
import withAuth from '../../../HOC/withAuth';
import { useTranslation } from '../../../t';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { isNumber } from '../../../util';
import { userStore } from '../../../stores/UserStore';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import OrderPreviewPanel from '../../../components/OrderPreviewPanel';
import PlatformCollectionPausedNotice, { canChangePlatformCollection } from '../../../components/PlatformCollectionPausedNotice';
import {
  OrderFormColumns,
  OrderFormGrid,
  OrderFormSection,
  OrderFormRow,
  OrderToggle,
  OrderTextInput,
  OrderNumberInput,
} from '../../../components/OrderFormControls';
import StepShell from '../StepShell';
import C from '../../../colors';
import { hasOrderSelections } from '../../../orderStatus';
import { stripFourByteUnicode } from '../../../legacyDatabaseText';

const Step1 = inject('snackbarStore', 'ordersStore', 'packageStore')(observer(({ snackbarStore, ordersStore, packageStore }) => {
  const { orderId } = useParams();
  const [order, setOrder] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const { t } = useTranslation();

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shootLocation, setShootLocation] = useState('');
  const [remark, setRemark] = useState('');
  const [shootTime, setShootTime] = useState('');
  const [packageId, setPackageId] = useState('');

  // Selection settings
  const [photoCount, setPhotoCount] = useState(0);
  const [photoCountError, setPhotoCountError] = useState('');
  const [forceLimit, setForceLimit] = useState(true);
  const [pricePerPic, setPricePerPic] = useState(0);
  const [pricePerPicError, setPricePerPicError] = useState('');
  const [showImageName, setShowImageName] = useState(false);
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);

  const validatePhotoCount = (v) => {
    if (!v) return t('photoCount can not be empty');
    if (!isNumber(v)) return t('photoCount must be number');
    if (v > 1000) return t('photoCount must be less than 1000');
    return '';
  };
  const validatePricePerPic = (v) => (!isNumber(v) ? t('pricePerPic must be number') : '');

  const autoSaveRequest = useCallback((data) => {
    if (!order.id) return;
    ordersStore.updateOrder(order.id, data).then((response) => {
      const updatedOrder = response.data?.order || response.data;
      if (updatedOrder?.id) {
        setOrder(current => ({
          ...current,
          ...updatedOrder,
          ...(updatedOrder.packageId == null ? { package: null, Package: null } : {}),
        }));
      }
      snackbarStore.showSnackbar(t('Update Success'));
    }).catch(() => {});
  }, [order.id, snackbarStore, ordersStore, t]);

  const autoSave = useMemo(() => debounce(autoSaveRequest, 500), [autoSaveRequest]);

  const handleInfoBlur = (e) => {
    const { name, value } = e.target;
    autoSave({ [name]: value });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const legacySafeValue = stripFourByteUnicode(value);
    if (name === 'photoCount') { setPhotoCount(value); setPhotoCountError(validatePhotoCount(value)); }
    else if (name === 'pricePerPic') { setPricePerPic(value); setPricePerPicError(validatePricePerPic(value)); }
    else if (name === 'customerName') setCustomerName(value);
    else if (name === 'customerPhone') setCustomerPhone(value);
    else if (name === 'shootLocation') setShootLocation(legacySafeValue);
    else if (name === 'remark') setRemark(legacySafeValue);
    else if (name === 'shootTime') { setShootTime(value); autoSave({ shootTime: value }); }
    else if (name === 'packageId') {
      setPackageId(value);
      const pkg = packageStore.packages.find(p => String(p.id) === value);
      setOrder(current => ({
        ...current,
        packageId: value || null,
        package: pkg || null,
        Package: pkg || null,
      }));
      if (pkg) { setPhotoCount(pkg.photoCount); autoSave({ packageId: value, photoCount: pkg.photoCount }); }
      else autoSave({ packageId: null });
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'photoCount') { const err = validatePhotoCount(value); if (!err) autoSave({ photoCount: value }); }
    else if (name === 'pricePerPic') { const err = validatePricePerPic(value); if (!err) autoSave({ pricePerPic: value }); }
  };

  useEffect(() => {
    if (!orderId) return;
    let active = true;
    setOrder({});
    setLoadFailed(false);
    ordersStore.fetchOrder(orderId).then(() => {
      if (!active) return;
      const o = ordersStore.currentOrder;
      if (!o || String(o.id) !== String(orderId)) {
        setLoadFailed(true);
        return;
      }
      setOrder(o);
      setCustomerName(o.customerName || '');
      setCustomerPhone(o.customerPhone || '');
      setShootLocation(o.shootLocation || '');
      setRemark(o.remark || '');
      setShootTime(o.shootTime ? dayjs(o.shootTime).format('YYYY-MM-DD') : '');
      setPackageId(o.packageId ? String(o.packageId) : '');
      setPhotoCount(o.photoCount);
      setForceLimit(o.forceLimit);
      setPricePerPic(o.pricePerPic);
      setShowImageName(o.showImageName || false);
      setIsOnlinePayment(o.isOnlinePayment || false);
    });
    packageStore.fetchPackages();
    return () => { active = false; };
  }, [orderId]);

  if (loadFailed) return <div style={{ padding: 40, color: C.ink3 }}>订单加载失败，请刷新重试</div>;
  if (!order.id) return <div style={{ padding: 40, color: C.ink3 }}>Loading...</div>;

  const photoTotal = order.Album?.pictureCount || 0;
  const hasSelections = hasOrderSelections(order);

  return (
    <StepShell order={order}>
      <OrderFormColumns preview={
        <OrderPreviewPanel showImageName={showImageName} forceLimit={forceLimit} isOnlinePayment={isOnlinePayment} price={pricePerPic} photoCount={photoCount} hasPackage={Boolean(packageId)}/>
      }>
          {hasSelections && (
            <div data-testid="selection-settings-advisory" style={{ padding: '11px 14px', marginTop: 8, marginBottom: 4, background: C.warningBg, border: `1px solid ${C.warningDot}`, borderRadius: 8, fontSize: 13, color: C.warningFg }}>
              已有客户提交选图。你仍可修改设置，但调整选片数量、加片规则或支付方式前，请确认不会影响现有客户。
            </div>
          )}

          <OrderFormSection>基本信息</OrderFormSection>

          <OrderFormGrid template={packageStore.packages.length > 0 ? 'minmax(0, 1fr) minmax(180px, .55fr)' : '1fr'}>
            {packageStore.packages.length > 0 && (
              <OrderFormRow label={t('Packages')} optional>
                <select data-testid="order-package-select" name="packageId" value={packageId} onChange={handleInputChange} style={{
                  width: '100%', padding: '9px 12px', borderRadius: 6,
                  border: `1px solid ${C.hair2}`, background: '#fff',
                  fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none',
                }}>
                  <option value="">— 不使用套餐 —</option>
                  {packageStore.packages.map(p => (
                    <option key={p.id} value={String(p.id)}>{p.name} · {p.photoCount}{t('pics')}</option>
                  ))}
                </select>
              </OrderFormRow>
            )}

            <OrderFormRow label={t('Shoot Time')} optional>
              <input type="date" name="shootTime" value={shootTime} onChange={handleInputChange}
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 6, border: `1px solid ${C.hair2}`, fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none', background: '#fff' }}/>
            </OrderFormRow>
          </OrderFormGrid>

          <OrderFormGrid>
            <OrderFormRow label={t('Customer Name')} required>
              <OrderTextInput name="customerName" value={customerName} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Customer Name')}/>
            </OrderFormRow>

            <OrderFormRow label={t('Customer Phone')} optional>
              <OrderTextInput name="customerPhone" value={customerPhone} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Customer Phone')}/>
            </OrderFormRow>
          </OrderFormGrid>

          <OrderFormSection>选片设置</OrderFormSection>

          <OrderFormRow label={t('show image name')} caption="在照片缩略图下显示原始文件名，方便客户沟通指定照片。" inline>
            <OrderToggle testId="order-show-image-name-toggle" label={t('show image name')} value={showImageName} onChange={(v) => { setShowImageName(v); autoSave({ showImageName: v }); }}/>
          </OrderFormRow>

          <OrderFormGrid>
            <OrderFormRow label={t('selectable images')} caption="客户最多可以勾选的照片数量。" required>
              <OrderNumberInput name="photoCount" value={photoCount} onChange={handleInputChange} onBlur={handleInputBlur}
                suffix={t('pics')} error={!!photoCountError} helperText={photoCountError || undefined}/>
            </OrderFormRow>

            <OrderFormRow label={t('force limit(no extra pics)')} caption="开启后允许客户超选并按加片价格计费。" inline last={forceLimit}>
              <OrderToggle testId="order-allow-extra-toggle" label={t('force limit(no extra pics)')} value={!forceLimit} onChange={(v) => { setForceLimit(!v); autoSave({ forceLimit: !v }); }}/>
            </OrderFormRow>
          </OrderFormGrid>

          {!forceLimit && (
            <OrderFormGrid>
              <OrderFormRow label={t('append picture price')} caption="客户超出上限的每张照片将按此价格计费。">
                <OrderNumberInput name="pricePerPic" value={pricePerPic} onChange={handleInputChange} onBlur={handleInputBlur}
                  prefix="¥" suffix={'/' + t('pics')} error={!!pricePerPicError} helperText={pricePerPicError || undefined}/>
              </OrderFormRow>

              <OrderFormRow label={t('Collected by platform')} caption={<PlatformCollectionPausedNotice/>} inline last>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <OrderToggle testId="order-online-payment-toggle" label={t('Collected by platform')} value={isOnlinePayment} disabled={!isOnlinePayment}
                    onChange={(v) => {
                      if (!canChangePlatformCollection(isOnlinePayment, v)) return;
                      setIsOnlinePayment(v);
                      autoSave({ isOnlinePayment: v });
                    }}/>
                  {!userStore.wxOpenId && (
                    <button type="button" onClick={() => userStore.setWechatDialogOpen(true)} style={{
                      padding: '8px 14px', borderRadius: 6, background: C.ink, color: '#fff',
                      border: 'none', fontFamily: C.sans, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}>{t('Link WeChat')}</button>
                  )}
                </div>
              </OrderFormRow>
            </OrderFormGrid>
          )}

          <OrderFormSection>补充信息</OrderFormSection>

          <OrderFormGrid>
            <OrderFormRow label={t('Shoot Location')} optional last>
              <OrderTextInput name="shootLocation" value={shootLocation} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Shoot Location')} multiline rows={2}/>
            </OrderFormRow>

            <OrderFormRow label={t('Remark')} optional last>
              <OrderTextInput name="remark" value={remark} onChange={handleInputChange} onBlur={handleInfoBlur} placeholder={t('Remark')} multiline rows={2}/>
            </OrderFormRow>
          </OrderFormGrid>
      </OrderFormColumns>
    </StepShell>
  );
}));

export default withAuth(Step1);
